import { useState } from 'react'
import type { EggSaleRecord } from '@henlytics/types'
import { Count, Money } from '../components/common/Money'
import { DataTable, type Column } from '../components/common/DataTable'
import { EmptyState } from '../components/common/EmptyState'
import { DateCell } from '../components/entry/DateCell'
import { EntryGrid } from '../components/entry/EntryGrid'
import { NumericCell } from '../components/entry/NumericCell'
import { useBatches } from '../hooks/useBatches'
import { useCreateSale, useSales, useVoidSale } from '../hooks/useSales'
import { today } from '../hooks/useReports'
import { clientUuid } from '../lib/uuid'

const POOLED = '__pooled__'

export function Sales() {
  const batches = useBatches()
  const sales = useSales()
  const create = useCreateSale()
  const voidSale = useVoidSale()

  const [saleDate, setSaleDate] = useState(today())
  const [target, setTarget] = useState(POOLED)
  const [grade, setGrade] = useState('')
  const [qtyEggs, setQtyEggs] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [totalAmount, setTotalAmount] = useState('')
  const [customer, setCustomer] = useState('')
  const [entryId, setEntryId] = useState(() => clientUuid())

  const columns: ReadonlyArray<Column<EggSaleRecord>> = [
    { key: 'date', header: 'Date', render: (s) => s.saleDate },
    {
      key: 'batch',
      header: 'Batch',
      render: (s) => (s.batchId ? (batches.data?.find((b) => b.id === s.batchId)?.code ?? s.batchId) : <em>pooled</em>),
    },
    { key: 'grade', header: 'Grade', render: (s) => s.grade ?? '—' },
    { key: 'qty', header: 'Eggs', numeric: true, render: (s) => <Count value={s.qtyEggs} /> },
    { key: 'unit', header: 'Unit price', numeric: true, render: (s) => <Money value={s.unitPrice} precision={3} /> },
    { key: 'total', header: 'Total', numeric: true, render: (s) => <Money value={s.totalAmount} /> },
    { key: 'customer', header: 'Customer', render: (s) => s.customer ?? '—' },
    {
      key: 'actions',
      header: '',
      render: (s) =>
        s.voidedAt ? (
          <span className="muted">voided</span>
        ) : (
          <button type="button" className="link-button" onClick={() => voidSale.mutate(s.id)} disabled={voidSale.isPending}>
            Void
          </button>
        ),
    },
  ]

  return (
    <div className="page">
      <header className="page-head">
        <h2>Egg sales</h2>
        <p className="muted">
          A pooled sale stays farm revenue rather than being split between batches by a guess. Proceeds from
          culls and bird sales are not entered here — they are salvage, recorded on the bird ledger and
          credited against batch cost (§3.8).
        </p>
      </header>

      <EntryGrid
        title="New sale"
        onSubmit={() =>
          create.mutate(
            {
              batchId: target === POOLED ? null : target,
              saleDate,
              grade: grade.trim() || null,
              qtyEggs: Number(qtyEggs || '0'),
              unitPrice,
              totalAmount,
              customer: customer.trim() || null,
              clientUuid: entryId,
            },
            {
              onSuccess: () => {
                setQtyEggs('')
                setUnitPrice('')
                setTotalAmount('')
                setCustomer('')
                setEntryId(clientUuid())
              },
            },
          )
        }
        submitLabel="Record sale"
        busy={create.isPending}
        error={create.isError ? create.error.message : null}
        success={create.isSuccess ? 'Recorded.' : null}
      >
        <DateCell label="Date" value={saleDate} onChange={setSaleDate} />
        <label className="cell">
          <span className="cell-label">Batch</span>
          <select className="cell-input" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value={POOLED}>Pooled — farm revenue</option>
            {(batches.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.code}
              </option>
            ))}
          </select>
        </label>
        <label className="cell">
          <span className="cell-label">Grade</span>
          <input className="cell-input" value={grade} onChange={(e) => setGrade(e.target.value)} />
        </label>
        <NumericCell label="Eggs" value={qtyEggs} onChange={setQtyEggs} integer />
        <NumericCell label="Unit price" value={unitPrice} onChange={setUnitPrice} />
        <NumericCell label="Total amount" value={totalAmount} onChange={setTotalAmount} />
        <label className="cell wide">
          <span className="cell-label">Customer</span>
          <input className="cell-input" value={customer} onChange={(e) => setCustomer(e.target.value)} />
        </label>
      </EntryGrid>

      <section>
        <h3>Sales</h3>
        <DataTable
          rows={sales.data ?? []}
          columns={columns}
          rowKey={(s) => s.id}
          rowClassName={(s) => (s.voidedAt ? 'voided' : undefined)}
          empty={<EmptyState title="No sales recorded yet." />}
        />
      </section>
    </div>
  )
}
